import { useEffect, useRef, useState } from "react";
import initRDKit from "@/utils/initRDKit";

interface MolecularStructureProps {
  smiles: string;
  width?: number;
  height?: number;
  className?: string;
}

const MolecularStructure = ({
  smiles,
  width = 200,
  height = 150,
  className = "",
}: MolecularStructureProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderMolecule = async () => {
      if (!smiles || !canvasRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        const rdkitModule = await initRDKit();

        const mol = rdkitModule.get_mol(smiles);
        if (!mol) {
          throw new Error("Invalid SMILES string");
        }

        // 분자 구조 그리기
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Canvas context not available");
        }

        // Canvas 초기화
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);

        // RDKit을 사용하여 분자 구조 SVG 생성
        const svg = mol.get_svg_with_highlights(JSON.stringify({}));

        // SVG를 Canvas에 그리기
        const img = new Image();
        const svgBlob = new Blob([svg], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          setIsLoading(false);
        };

        img.onerror = () => {
          throw new Error("Failed to render molecule image");
        };

        img.src = url;

        // 메모리 정리
        mol.delete();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "분자 구조를 렌더링할 수 없습니다",
        );
        setIsLoading(false);

        // 오류 시 기본 메시지 표시
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = "#f3f4f6";
            ctx.fillRect(0, 0, width, height);
            ctx.fillStyle = "#6b7280";
            ctx.font = "12px Arial";
            ctx.textAlign = "center";
            ctx.fillText("구조 로딩 실패", width / 2, height / 2);
          }
        }
      }
    };

    renderMolecule();
  }, [smiles, width, height]);

  if (!smiles) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 border border-gray-200 rounded ${className}`}
        style={{ width, height }}
      >
        <span className="text-xs text-gray-500">SMILES 없음</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="border border-gray-200 rounded bg-white"
      />
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded"
          style={{ width, height }}
        >
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs text-gray-500 mt-1">로딩 중...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-1">
          <span className="text-xs text-red-500">{error}</span>
        </div>
      )}
    </div>
  );
};

export default MolecularStructure;
